/**
 * VideoLibraryContext — Phase 3 (PRI-291)
 *
 * Bridge context: PrimateVisionV2Inner owns the state and populates this context.
 * Child components consume via useVideoLibraryContext() instead of receiving props.
 *
 * Phase 3 approach: "bridge" pattern — state stays in PrimateVisionV2Inner,
 * context exposes it so prop drilling is eliminated for child components.
 * Phase 5 will move the hook calls into the provider.
 */

import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { VideoEntry, DemoVideoLibraryEntry } from '../hooks/types'
import type { UserVideo } from '../lib/api'

export interface VideoLibraryContextValue {
  // Library state
  savedUserVideos: UserVideo[]
  demoVideos: DemoVideoLibraryEntry[]
  regularUserVideos: UserVideo[]
  streamUserVideos: UserVideo[]

  // Queue
  videoQueue: VideoEntry[]
  activeVideoId: string | null
  uploadProgress: number

  // Selection (selectedDemoVideo is a string ID, not the full entry)
  selectedFile: File | null
  selectedDemoVideo: string | null
  selectedSavedVideo: UserVideo | null

  // Actions
  selectFile: (f: File | null) => void
  selectDemoVideo: (v: string | null) => void
  selectSavedVideo: (v: UserVideo | null) => void
  clearSelection: () => void
  deleteVideo: (id: string) => void
}

const VideoLibraryContext = createContext<VideoLibraryContextValue | null>(null)

export function VideoLibraryContextProvider({
  children,
  value,
}: {
  children: ReactNode
  value: VideoLibraryContextValue
}) {
  return <VideoLibraryContext.Provider value={value}>{children}</VideoLibraryContext.Provider>
}

export function useVideoLibraryContext(): VideoLibraryContextValue {
  const ctx = useContext(VideoLibraryContext)
  if (!ctx) throw new Error('useVideoLibraryContext must be used inside VideoLibraryContextProvider')
  return ctx
}

export default VideoLibraryContext
